

import React, { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../../hooks/useFilters';
import { useTags } from '../../../hooks/useTags';
import { useThemeStore } from '../../../stores/themeStore';
import { useAllContent } from '../../../hooks/useAllContent';
import { Icon } from '../../shared/Icon/Icon';

import styles from './AdditionalFilter.module.less';
import { useAdditionalFilterStore } from '../../../stores/additionalFilterStore';
import useIsMobile from '../../../hooks/useIsMobile';

const AdditionalFilter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { 
    isOpenFilterDropdown, 
    toggleDropdown, 
    closeDropdown,
    tempFilters,
    setTempFilters,
    handleTempTagChange,
    handleClear: handleStoreClear
  } = useAdditionalFilterStore();

  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpenFilterDropdown) return;
    
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpenFilterDropdown, closeDropdown]);

  const {
    filters,
    selectedCategory,
    selectedSubcategories,
    selectedYears,
    handleFilterChange,
    handleArchiveChange,
    clearAllFilters,
  } = useFilters();

  const { data: coursesData } = useAllContent({
    selectedCategory,
    selectedSubcategories,
    filters,
    selectedYears,
  });

  const { data: apiTags, isLoading, error } = useTags();

  const { theme } = useThemeStore();

  useEffect(() => {
    if (isOpenFilterDropdown && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpenFilterDropdown]);

  const tagsToUse = useMemo(() => {
    if (apiTags && apiTags?.length > 0) {
      return apiTags.map((tag) => ({
        id: tag.id,
        name:
          tag.translations.find((t) => t.languages_code === i18n.language)
            ?.tag || tag.tag_name,
      }));
    }
    return [];
  }, [apiTags, i18n.language]);

  const selectedTags = useMemo(() => {
    return tagsToUse.filter((tag) => filters[tag.id]);
  }, [tagsToUse, filters]);

  const handleTempTagClick = (e: React.MouseEvent, tagId: string) => {
    e.preventDefault();
    e.stopPropagation();
    handleTempTagChange(tagId);
  };
  const handleToggleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const tagsObject: Record<string, boolean> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (key !== 'archive' && typeof value === 'boolean') {
        tagsObject[key] = value;
      }
    });

    setTempFilters({
      archiveStatus: filters.archiveStatus as 'current' | 'archive' | null,
      tags: tagsObject,
    });

    toggleDropdown();
  };

  const handleShow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    Object.entries(tempFilters.tags).forEach(([tagId, isSelected]) => {
      if (tagId !== 'archive') {
        handleFilterChange(tagId, isSelected);
      }
    });

    if (tempFilters.archiveStatus) {
      handleArchiveChange(tempFilters.archiveStatus);
    }
    closeDropdown();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    handleStoreClear();
    clearAllFilters();
  };

  const handleDropdownClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    closeDropdown();
  };

  if (isLoading || error) return null;

  return (
    <div
      className={`${styles.additionalFilters} ${theme !== 'light' && styles.additionalFilters__dark}`}
    >
      <div
        onClick={handleToggleOpen}
        className={styles.additionalFiltersDropdownToggle}
      >
        <button className={styles.additionalFiltersDropdownToggleButton}>
          <span className={styles.savedContentNumber}>
            {filters.archiveStatus
              ? selectedTags.length > 0
                ? selectedTags.length + 1
                : 1
              : selectedTags.length > 0 && selectedTags.length}
          </span>
          <Icon
            id="filter"
            className={`${styles.icon} ${theme !== 'light' && styles.iconLight} ${
              (filters.archiveStatus || selectedTags.length > 0) &&
              styles.iconActive
            }`}
          />
        </button>
        {coursesData?.pages[0].data.length} {t('from')}{' '}
        {coursesData?.pages[0].totalItems} {t('posts')}
      </div>

      {isOpenFilterDropdown && (
        <div className={styles.additionalFiltersDropdown} ref={menuRef}>
          <div
            className={styles.additionalFiltersDropdownContent}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div className={styles.additionalFiltersDropdownHeader}>
              <h2 className={styles.additionalFiltersTitle}>
                {t('Content type')}
              </h2>
              <button
                className={styles.additionalFiltersCloseButton}
                onClick={handleDropdownClose}
              >
                <Icon id="close-dropdown" className={styles.iconClose} />
              </button>
            </div>
            <div className={styles.additionalFiltersButtonsContainer}>
              <div className={styles.additionalFiltersFilterContainer}>
                <div className={styles.additionalFiltersFilterButtons}>
                  {tagsToUse.map((tag) => (
                    <li
                      key={tag.id}
                      className={styles.additionalFiltersFilterItem}
                    >
                      <button
                        onClick={(e) => handleTempTagClick(e, tag.id)}
                        className={`
                  ${styles.additionalFiltersFilterButton}
                  ${tempFilters.tags[tag.id] ? styles.additionalFiltersFilterButtonActive : ''}
                `}
                      >
                        {tag.name}
                        {tempFilters.tags[tag.id] && (
                          <Icon
                            id="close-dropdown"
                            className={styles.iconCross}
                          />
                        )}
                      </button>
                    </li>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.additionalFiltersBtnContainer}>
            <button
              className={styles.additionalFiltersShowBtn}
              onClick={handleShow}
            >
              {t('Show')}
            </button>
            <button
              onClick={handleClear}
              className={styles.additionalFiltersClearBtn}
            >
              {t('Clear filters')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalFilter;
